import { useState, useEffect } from 'react';
import { X, Plus, Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth, parseISO, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAppointmentsStore } from '@/store/appointmentsStore';
import { AppointmentModal } from '@/components/calendar/AppointmentModal';

interface LeadFullCalendarProps {
  leadId: string;
  leadName?: string;
  lojaId?: string;
  open: boolean;
  onClose: () => void;
}

export function LeadFullCalendar({ leadId, leadName, lojaId, open, onClose }: LeadFullCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedApptId, setSelectedApptId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { appointments, fetchByLead } = useAppointmentsStore();

  useEffect(() => {
    if (open && leadId) {
      fetchByLead(leadId);
    }
  }, [open, leadId, fetchByLead]);

  if (!open) return null;

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days: Date[] = [];
  let day = calendarStart;
  while (day <= calendarEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  const getAppointmentsForDay = (date: Date) => {
    return appointments.filter(a => {
      if (!a.agendado_para || a.status === 'cancelado') return false;
      const dateObj = typeof a.agendado_para === 'string' ? parseISO(a.agendado_para) : new Date(a.agendado_para);
      return isSameDay(dateObj, date);
    });
  };

  const upcomingAppts = appointments
    .filter(a => a.agendado_para && a.status !== 'cancelado')
    .sort((a, b) => new Date(a.agendado_para).getTime() - new Date(b.agendado_para).getTime());

  const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: ptBR });
  const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                Agenda Completa
              </h2>
              {leadName && (
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                  • {leadName}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSelectedDate(new Date());
                  setSelectedApptId(null);
                  setModalOpen(true);
                }}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" /> Novo Agendamento
              </button>
              <button 
                onClick={onClose} 
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 overflow-y-auto">
            {/* Month selector */}
            <div className="flex items-center justify-between mb-4 px-1">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-600 dark:text-gray-300 cursor-pointer transition-colors"
                title="Mês anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <h3 className="font-bold text-sm text-gray-900 dark:text-white">
                {capitalizedMonth}
              </h3>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-600 dark:text-gray-300 cursor-pointer transition-colors"
                title="Próximo mês"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                <div key={d} className="text-center text-xs font-bold text-gray-400 dark:text-gray-500 py-1">
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map(d => {
                const dayAppts = getAppointmentsForDay(d);
                const isToday = isSameDay(d, new Date());
                const isCurrentMonth = isSameMonth(d, currentMonth);
                return (
                  <div
                    key={d.toISOString()}
                    onClick={() => {
                      setSelectedDate(d);
                      setSelectedApptId(null);
                      setModalOpen(true);
                    }}
                    className={`min-h-[68px] p-1.5 border border-gray-100 dark:border-gray-800/80 rounded-xl cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition-all ${
                      !isCurrentMonth ? 'opacity-35' : ''
                    } ${isToday ? 'ring-2 ring-blue-500 bg-blue-50/20 dark:bg-blue-900/10 font-bold' : ''}`}
                  >
                    <div className="text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-1 flex items-center justify-between">
                      <span>{format(d, 'd')}</span>
                      {dayAppts.length > 0 && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      )}
                    </div>
                    {dayAppts.slice(0, 2).map(a => (
                      <div 
                        key={a.id} 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedApptId(a.id);
                          setModalOpen(true);
                        }}
                        className="text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded-md px-1.5 py-0.5 mb-1 truncate font-medium hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors shadow-2xs"
                      >
                        {format(new Date(a.agendado_para), 'HH:mm')} {a.titulo || 'Agendamento'}
                      </div>
                    ))}
                    {dayAppts.length > 2 && (
                      <div className="text-[9px] text-gray-500 font-bold px-1">
                        +{dayAppts.length - 2} mais
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* List of upcoming appointments below */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800 max-h-[200px] overflow-y-auto bg-gray-50/50 dark:bg-[#111B21]/50">
            <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
              Todos os Agendamentos do Lead ({upcomingAppts.length})
            </h4>
            {upcomingAppts.length === 0 ? (
              <p className="text-xs text-gray-400 py-3 text-center">Nenhum agendamento registrado para este lead.</p>
            ) : (
              <div className="space-y-1.5">
                {upcomingAppts.map(a => (
                  <div 
                    key={a.id} 
                    onClick={() => {
                      setSelectedApptId(a.id);
                      setModalOpen(true);
                    }}
                    className="flex items-center justify-between p-2.5 bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-800 hover:border-blue-500/50 rounded-xl cursor-pointer transition-all shadow-2xs group"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Clock className="w-4 h-4 text-blue-500 shrink-0 group-hover:scale-110 transition-transform" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-gray-900 dark:text-white truncate">
                          {a.titulo || 'Agendamento'}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {format(new Date(a.agendado_para), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 capitalize">
                      {a.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {modalOpen && (
            <AppointmentModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              appointmentId={selectedApptId}
              initialLeadId={leadId}
              initialDateTime={selectedDate}
              lojaId={lojaId}
              onSaved={() => {
                setModalOpen(false);
                fetchByLead(leadId);
              }}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
